import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { isCell } from '@/lib/cell/config';
import { OTP_DIGITS } from '@/lib/otp';

/**
 * Lands here from the magic-link click. The Supabase JS client has
 * `detectSessionInUrl: true` set, so it will auto-exchange the
 * `?code=…` PKCE code for a session on init. We just wait for the
 * session to materialise (or surface an error).
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // CELL (M28): no email links on the cell build (Cognito sign-in is on /login), so this page just goes home.
      if (isCell()) { if (!cancelled) navigate('/', { replace: true }); return; }
      // Give the SDK a tick to process the URL.
      const { data: existing } = await supabase.auth.getSession();
      if (existing.session) {
        if (!cancelled) navigate('/', { replace: true });
        return;
      }
      // Subscribe — auth-state-change fires once exchange completes. (CELL (M28): not reached, returned above.)
      const { data: sub } = supabase.auth.onAuthStateChange((_evt, s) => {
        if (cancelled) return;
        if (s) navigate('/', { replace: true });
      });
      // Hard timeout so we don't spin forever on a busted link.
      const timer = window.setTimeout(() => {
        if (!cancelled) {
          setError(
            "Sign-in link didn't work — mail apps sometimes use it up before you tap. " +
            `Go back to sign in, request a new email, and type the ${OTP_DIGITS}-digit code from it instead.`
          );
        }
      }, 8000);
      return () => {
        sub.subscription.unsubscribe();
        window.clearTimeout(timer);
      };
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-brand-fog grid place-items-center px-6">
      <div className="card max-w-sm w-full text-center">
        {error ? (
          <>
            <h2 className="font-display text-xl text-brand-deep mb-2">Hmm.</h2>
            <p className="text-sm text-brand-slate">{error}</p>
            <button
              className="btn-primary mt-6 w-full"
              onClick={() => navigate('/login', { replace: true })}
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <h2 className="font-display text-xl text-brand-deep mb-2">
              Signing you in…
            </h2>
            <p className="text-sm text-brand-slate">One sec.</p>
          </>
        )}
      </div>
    </div>
  );
}
